import { useState, useCallback } from 'react';
import { detectDeviceType } from '../utils/device';
import { issueToken } from '../utils/tokenStorage';

const UUID_RECEIVED_EVENT = 'uuid-received';

const getAndroidUUID = () =>
  new Promise((resolve, reject) => {
    try {
      if (window.getUserUUID?.postMessage) {
        const handler = (event) => {
          window.removeEventListener(UUID_RECEIVED_EVENT, handler);
          clearTimeout(tid);
          resolve(event.detail.uuid);
        };
        window.addEventListener(UUID_RECEIVED_EVENT, handler);
        window.getUserUUID.postMessage('');
        const tid = setTimeout(() => {
          window.removeEventListener(UUID_RECEIVED_EVENT, handler);
          reject(new Error('Android UUID request timeout'));
        }, 5000);
      } else {
        let retryCount = 0;
        const check = () => {
          if (window.getUserUUID?.postMessage) {
            window.getUserUUID.postMessage('');
          } else if (retryCount < 10) {
            retryCount++;
            setTimeout(check, 100);
          } else {
            reject(new Error('Android interface not available'));
          }
        };
        check();
      }
    } catch (error) {
      reject(error);
    }
  });

const getIOSUUID = () =>
  new Promise((resolve, reject) => {
    try {
      if (window.webkit?.messageHandlers?.getUserUUID) {
        const handler = (event) => {
          window.removeEventListener(UUID_RECEIVED_EVENT, handler);
          clearTimeout(tid);
          resolve(event.detail.uuid);
        };
        window.addEventListener(UUID_RECEIVED_EVENT, handler);
        window.webkit.messageHandlers.getUserUUID.postMessage({});
        const tid = setTimeout(() => {
          window.removeEventListener(UUID_RECEIVED_EVENT, handler);
          reject(new Error('iOS UUID request timeout'));
        }, 5000);
      } else {
        reject(new Error('iOS interface not available'));
      }
    } catch (error) {
      reject(error);
    }
  });

/**
 * UUID 획득 → 토큰 발급 흐름을 처리하는 커스텀 훅.
 * 반환값: { uuid, accessToken, deviceType, initAuth }
 * 컴포넌트에서 accessToken 변화를 useEffect로 감지해 후속 작업을 처리하세요.
 */
const useAuth = () => {
  const [uuid, setUuid] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [deviceType, setDeviceType] = useState(null);

  const tryIssueToken = useCallback(async (resolvedUuid) => {
    try {
      const token = await issueToken(resolvedUuid);
      setAccessToken(token);
      return token;
    } catch (error) {
      console.error('Error issuing token:', error);
      return null;
    }
  }, []);

  const initAuth = useCallback(async () => {
    const device = detectDeviceType();
    setDeviceType(device);

    try {
      let resolvedUuid = null;

      if (device === 'android') resolvedUuid = await getAndroidUUID();
      else if (device === 'ios') resolvedUuid = await getIOSUUID();

      if (resolvedUuid) {
        setUuid(resolvedUuid);
        localStorage.setItem('lastKnownUUID', resolvedUuid);
        localStorage.setItem('lastKnownDevice', device);
        await tryIssueToken(resolvedUuid);
        return resolvedUuid;
      }
    } catch (error) {
      console.error('Error getting UUID:', error);

      const backupUUID = localStorage.getItem('lastKnownUUID');
      if (backupUUID) {
        console.log('Using backup UUID:', backupUUID);
        setUuid(backupUUID);
        await tryIssueToken(backupUUID);
        return backupUUID;
      }

      const tempUUID = 'temp-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
      console.warn('Using temporary UUID:', tempUUID);
      setUuid(tempUUID);
      await tryIssueToken(tempUUID);
      return tempUUID;
    }

    return null;
  }, [tryIssueToken]);

  return { uuid, accessToken, deviceType, initAuth };
};

export default useAuth;
